import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Map as MapboxMap, Marker, Popup } from 'react-map-gl/mapbox'
import { useMantineColorScheme } from '@mantine/core'
import 'mapbox-gl/dist/mapbox-gl.css'
import './DriveMapView.css'
import { MAPBOX_PUBLIC_TOKEN } from '@/constants'
import { determineMapCenter } from '@/components/Navigation/utils/mapUtils'
import { contrastTextColor } from '@/components/Navigation/utils/mapGLHelpers'
import type { DriveMapDrive } from './useDriveMapData'

type Props = {
  drives: DriveMapDrive[]
  vehicleColors: Record<string, string>
  hiddenIds: Set<string>
}

type PopupInfo = {
  longitude: number
  latitude: number
  text: string
  vehicleId: string
}

const markerBaseStyle = (color: string): React.CSSProperties => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: color,
  color: contrastTextColor(color),
  fontWeight: 700,
  fontSize: 13,
  boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  border: '1px solid rgba(0,0,0,0.15)',
})

type MarkerProps = {
  longitude: number
  latitude: number
  color: string
  label: number
  popupText: string
  vehicleId: string
  isDestination: boolean
  hidden: boolean
  onSelect: (info: PopupInfo) => void
}

// Memoized so toggling vehicle visibility only re-renders markers whose
// `hidden` flag actually flipped. All props are primitives or stable refs.
const DriveMarker = memo(function DriveMarker({
  longitude,
  latitude,
  color,
  label,
  popupText,
  vehicleId,
  isDestination,
  hidden,
  onSelect,
}: MarkerProps) {
  const style = isDestination
    ? { ...markerBaseStyle(color), outline: '2px dashed rgba(255,255,255,0.85)', outlineOffset: -4 }
    : markerBaseStyle(color)
  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      anchor="center"
      // Hide via CSS on the Mapbox wrapper itself. Putting pointer-events
      // only on the inner div leaks clicks: the wrapper still captures them
      // and steals clicks from visible markers underneath.
      style={{
        visibility: hidden ? 'hidden' : 'visible',
        pointerEvents: hidden ? 'none' : 'auto',
      }}
      onClick={e => {
        e.originalEvent.stopPropagation()
        onSelect({ longitude, latitude, text: popupText, vehicleId })
      }}
    >
      <div style={style}>{label}</div>
    </Marker>
  )
})

const computeBounds = (drives: DriveMapDrive[]) => {
  let minLng = Infinity
  let maxLng = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity
  for (const d of drives) {
    for (const p of [d.origin_point, d.destination_point]) {
      if (p.longitude < minLng) minLng = p.longitude
      if (p.longitude > maxLng) maxLng = p.longitude
      if (p.latitude < minLat) minLat = p.latitude
      if (p.latitude > maxLat) maxLat = p.latitude
    }
  }
  if (!Number.isFinite(minLng)) return null
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ] as [[number, number], [number, number]]
}

export function DriveMapView({ drives, vehicleColors, hiddenIds }: Props) {
  const { colorScheme } = useMantineColorScheme()
  const [activePopup, setActivePopup] = useState<PopupInfo | null>(null)

  const mapStyle = useMemo(
    () => `mapbox://styles/mapbox/${colorScheme === 'light' || colorScheme === 'auto' ? 'streets-v12' : 'dark-v11'}`,
    [colorScheme]
  )

  // Group drives by vehicle so per-vehicle numbering stays stable.
  const drivesByVehicle = useMemo(() => {
    const grouped = new Map<string, DriveMapDrive[]>()
    for (const d of drives) {
      const arr = grouped.get(d.vehicle_id) ?? []
      arr.push(d)
      grouped.set(d.vehicle_id, arr)
    }
    for (const arr of grouped.values()) {
      arr.sort((a, b) => a.start_after.localeCompare(b.start_after))
    }
    return grouped
  }, [drives])

  // Compute initial bounds once on mount. react-map-gl honors initialViewState
  // only on the first render, so the camera stays put through later toggles
  // (the user can pan/zoom freely without being yanked back).
  const initialViewState = useMemo(() => {
    const bounds = computeBounds(drives)
    if (bounds) {
      return { bounds, fitBoundsOptions: { padding: 80, maxZoom: 15 } }
    }
    return { zoom: 12, ...determineMapCenter() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Stable so memo'd markers do not re-render every time the parent renders.
  const handleSelect = useCallback((info: PopupInfo) => setActivePopup(info), [])

  // Close the popup if its vehicle gets hidden.
  useEffect(() => {
    if (activePopup && hiddenIds.has(activePopup.vehicleId)) setActivePopup(null)
  }, [hiddenIds, activePopup])

  return (
    <MapboxMap
      id="drive-map"
      mapboxAccessToken={MAPBOX_PUBLIC_TOKEN}
      mapStyle={mapStyle}
      initialViewState={initialViewState}
      style={{ width: '100%', height: '100%' }}
      dragPan
      scrollZoom
      touchZoomRotate
      doubleClickZoom
      onClick={() => setActivePopup(null)}
    >
      {Array.from(drivesByVehicle.entries()).flatMap(([vehicleId, vehicleDrives]) => {
        const color = vehicleColors[vehicleId] ?? '#868e96'
        const hidden = hiddenIds.has(vehicleId)
        return vehicleDrives.flatMap((drive, i) => [
          <DriveMarker
            key={`${drive.id}-origin`}
            longitude={drive.origin_point.longitude}
            latitude={drive.origin_point.latitude}
            color={color}
            label={2 * i + 1}
            popupText={`${vehicleId}, start of route ${drive.route_id}`}
            vehicleId={vehicleId}
            isDestination={false}
            hidden={hidden}
            onSelect={handleSelect}
          />,
          <DriveMarker
            key={`${drive.id}-destination`}
            longitude={drive.destination_point.longitude}
            latitude={drive.destination_point.latitude}
            color={color}
            label={2 * (i + 1)}
            popupText={`${vehicleId}, end of route ${drive.route_id}`}
            vehicleId={vehicleId}
            isDestination={true}
            hidden={hidden}
            onSelect={handleSelect}
          />,
        ])
      })}

      {activePopup && (
        <Popup
          longitude={activePopup.longitude}
          latitude={activePopup.latitude}
          anchor="bottom"
          offset={20}
          closeButton={false}
          closeOnClick={false}
          onClose={() => setActivePopup(null)}
          className="drive-map-popup"
          maxWidth="none"
        >
          <div style={{ padding: '14px 18px', whiteSpace: 'nowrap' }}>{activePopup.text}</div>
        </Popup>
      )}
    </MapboxMap>
  )
}
